import {
  CloseOutlined,
  FilePdfOutlined,
  InboxOutlined,
  PlusOutlined,
  UploadOutlined,
} from '@ant-design/icons'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  Button,
  DatePicker,
  Flex,
  Form,
  Image,
  Input,
  message,
  Modal,
  Select,
  Spin,
  Upload,
} from 'antd'
import dayjs from 'dayjs'
import { CreatePropertyActivityPayload, PropertyActivityItem, PropertyActivityType } from 'Modules'
import { useEffect, useMemo, useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import MarkdownEditor from 'component/markdown-editor/markdown-editor'
import {
  ALLOWED_FILE_EXT_IMAGE,
  ALLOWED_FILE_EXT_PDF,
  ALLOWED_MIME_TYPES_ATTACHMENT,
} from 'constants/mime-type'
import {
  getActivityTypeId,
  getActivityTypeName,
  PROPERTY_ACTIVITY_LIMITS,
} from 'constants/property-activities'
import { logError } from 'utils/logger'
import { uploadPropertyActivityAttachment, uploadPropertyActivityImages } from 'utils/upload'

const { Dragger } = Upload

const {
  MAX_IMAGES,
  MAX_DOCUMENTS,
  MAX_UPLOAD_FILE_SIZE_MB,
  MAX_ICON_SIZE_MB,
  TITLE_MAX,
  DESCRIPTION_MAX,
} = PROPERTY_ACTIVITY_LIMITS

const getFileNameFromUrl = (url: string): string => {
  try {
    const u = new URL(url)
    return decodeURIComponent(u.pathname.split('/').pop() || 'file')
  } catch {
    return url.split('/').pop()?.split('?')[0] || 'file'
  }
}

interface ActivityFormModalProps {
  open: boolean
  ptoId: string
  ptoName?: string
  activity?: PropertyActivityItem | null
  onClose: () => void
}

interface ActivityFormValues {
  typeId: string
  title: string
  description: string
  timeline: dayjs.Dayjs
}

const uploadOneImage = async (file: File): Promise<string> => {
  const urls = await uploadPropertyActivityImages([file])
  if (!urls[0]) throw new Error('Upload returned no URL')
  return urls[0]
}

export const ActivityFormModal = ({
  open,
  ptoId,
  ptoName,
  activity,
  onClose,
}: ActivityFormModalProps) => {
  const [form] = Form.useForm<ActivityFormValues>()
  const queryClient = useQueryClient()

  const [loading, setLoading] = useState(false)

  const [imageUrls, setImageUrls] = useState<string[]>([])
  const [documentUrls, setDocumentUrls] = useState<string[]>([])
  const [uploadingImages, setUploadingImages] = useState(0)
  const [uploadingDocuments, setUploadingDocuments] = useState(0)

  const [addTypeOpen, setAddTypeOpen] = useState(false)
  const [newTypeName, setNewTypeName] = useState('')
  const [newTypeIcon, setNewTypeIcon] = useState<string>('')
  const [uploadingIcon, setUploadingIcon] = useState(false)
  const [savingType, setSavingType] = useState(false)

  const isEdit = !!activity

  const { data: typesData } = useQuery({
    queryKey: ['propertyActivities', 'types'],
    queryFn: async () => {
      const res = await AppAdmin.getPropertyActivityTypes()
      return (res.data?.data ?? []) as PropertyActivityType[]
    },
    enabled: open,
  })

  // BE returns `typeId` populated as an object on GET; helper normalizes to id string.
  const activityTypeId = getActivityTypeId(activity)
  const activityTypeName = getActivityTypeName(activity)

  // Include activity's current type as fallback so Select can display the
  // selected value even before `typesData` finishes loading (or if the type
  // was deleted but the activity still references it).
  const typeOptions = useMemo(() => {
    const types = typesData ?? []
    const baseOptions = types.map((t) => ({ label: t.name, value: t._id }))
    if (activityTypeId && !types.some((t) => t._id === activityTypeId)) {
      baseOptions.unshift({
        label: activityTypeName ?? activityTypeId,
        value: activityTypeId,
      })
    }
    return baseOptions
  }, [typesData, activityTypeId, activityTypeName])

  useEffect(() => {
    if (!open) return

    form.resetFields()

    if (activity) {
      form.setFieldsValue({
        typeId: activityTypeId,
        title: activity.title,
        description: activity.description,
        timeline: activity.timeline ? dayjs(activity.timeline) : undefined,
      })
      setImageUrls(activity.images ?? [])
      setDocumentUrls(activity.documents ?? [])
    } else {
      setImageUrls([])
      setDocumentUrls([])
    }
    setAddTypeOpen(false)
    setNewTypeName('')
    setNewTypeIcon('')
  }, [activity, activityTypeId, open, form])

  const performClose = () => {
    form.resetFields()
    setImageUrls([])
    setAddTypeOpen(false)
    setNewTypeName('')
    setNewTypeIcon('')
    onClose()
  }

  const handleClose = () => {
    if (loading) return

    const hasUnsavedChanges =
      form.isFieldsTouched() ||
      (!isEdit && imageUrls.length > 0) ||
      addTypeOpen ||
      !!newTypeName ||
      !!newTypeIcon

    if (!hasUnsavedChanges) {
      performClose()
      return
    }

    Modal.confirm({
      title: 'Discard changes?',
      content: 'Your unsaved changes will be lost.',
      okText: 'Discard',
      okButtonProps: { color: 'danger', variant: 'solid' },
      cancelText: 'Continue Editing',
      centered: true,
      onOk: performClose,
    })
  }

  const buildPayload = (values: ActivityFormValues): CreatePropertyActivityPayload => {
    const payload: CreatePropertyActivityPayload = {
      ptoId,
      typeId: values.typeId,
      title: values.title.trim(),
      description: values.description.trim(),
      timeline: values.timeline.toISOString(),
    }
    if (imageUrls.length > 0) {
      payload.images = imageUrls
    }
    if (documentUrls.length > 0) {
      payload.documents = documentUrls
    }
    return payload
  }

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields()
      setLoading(true)

      const payload = buildPayload(values)

      if (isEdit) {
        await AppAdmin.updatePropertyActivity(activity!._id, payload)
        message.success('Activity updated successfully')
      } else {
        await AppAdmin.createPropertyActivity(payload)
        message.success('Activity created successfully')
      }

      queryClient.invalidateQueries({ queryKey: ['propertyActivities', 'activities', ptoId] })
      queryClient.invalidateQueries({ queryKey: ['propertyActivities', 'ptos'] })

      form.resetFields()
      setImageUrls([])
      onClose()
    } catch (e: any) {
      if (e?.errorFields) return
      message.error(e?.message || 'Save activity failed')
      logError(e)
    } finally {
      setLoading(false)
    }
  }

  const beforeUploadImage = async (file: File) => {
    if (!ALLOWED_MIME_TYPES_ATTACHMENT.includes(file.type)) {
      message.error('Only PNG, JPG or PDF allowed')
      return Upload.LIST_IGNORE
    }
    if (file.size / 1024 / 1024 > MAX_UPLOAD_FILE_SIZE_MB) {
      message.error(`File must be smaller than ${MAX_UPLOAD_FILE_SIZE_MB}MB`)
      return Upload.LIST_IGNORE
    }
    if (imageUrls.length + uploadingImages >= MAX_IMAGES) {
      message.error(`Maximum ${MAX_IMAGES} files`)
      return Upload.LIST_IGNORE
    }

    setUploadingImages((n) => n + 1)
    try {
      const url = await uploadPropertyActivityAttachment(file)
      setImageUrls((prev) => [...prev, url])
    } catch (err: any) {
      message.error(err?.message || 'Upload file failed')
      logError(err)
    } finally {
      setUploadingImages((n) => n - 1)
    }

    return false
  }

  const beforeUploadDocument = async (file: File) => {
    if (file.type !== 'application/pdf') {
      message.error('Only PDF allowed')
      return Upload.LIST_IGNORE
    }
    if (file.size / 1024 / 1024 > MAX_UPLOAD_FILE_SIZE_MB) {
      message.error(`File must be smaller than ${MAX_UPLOAD_FILE_SIZE_MB}MB`)
      return Upload.LIST_IGNORE
    }
    if (documentUrls.length + uploadingImages >= MAX_DOCUMENTS) {
      message.error(`Maximum ${MAX_DOCUMENTS} files`)
      return Upload.LIST_IGNORE
    }

    setUploadingDocuments((n) => n + 1)
    try {
      const url = await uploadPropertyActivityAttachment(file)
      setDocumentUrls((prev) => [...prev, url])
    } catch (err: any) {
      message.error(err?.message || 'Upload file failed')
      logError(err)
    } finally {
      setUploadingDocuments((n) => n - 1)
    }

    return false
  }

  const beforeUploadIcon = async (file: File) => {
    const isSvg = file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')
    if (!isSvg) {
      message.error('Only SVG files allowed')
      return Upload.LIST_IGNORE
    }
    if (file.size / 1024 / 1024 > MAX_ICON_SIZE_MB) {
      message.error(`Icon must be smaller than ${MAX_ICON_SIZE_MB}MB`)
      return Upload.LIST_IGNORE
    }

    setUploadingIcon(true)
    try {
      const url = await uploadOneImage(file)
      setNewTypeIcon(url)
    } catch (err: any) {
      message.error(err?.message || 'Upload icon failed')
      logError(err)
    } finally {
      setUploadingIcon(false)
    }

    return false
  }

  const handleSaveType = async () => {
    const name = newTypeName.trim()
    if (!name) {
      message.error('Type Name is required')
      return
    }
    try {
      setSavingType(true)
      const res = await AppAdmin.createPropertyActivityType({
        name,
        icon: newTypeIcon || undefined,
      })
      const created = res.data?.data as PropertyActivityType
      message.success('Activity type added successfully')
      queryClient.invalidateQueries({ queryKey: ['propertyActivities', 'types'] })
      form.setFieldValue('typeId', created._id)
      setAddTypeOpen(false)
      setNewTypeName('')
      setNewTypeIcon('')
    } catch (e: any) {
      message.error(e?.message || 'Add activity type failed')
      logError(e)
    } finally {
      setSavingType(false)
    }
  }

  const titleText = isEdit ? 'Edit Activity' : 'Create New Activity'
  const subtitleText = isEdit
    ? `Update activity details for ${ptoName ?? 'PTO'}`
    : `Post an activity update for ${ptoName ?? 'PTO'}`

  const removeImage = (idx: number) => {
    setImageUrls((prev) => prev.filter((_, i) => i !== idx))
  }

  const removeDocument = (idx: number) => {
    setDocumentUrls((prev) => prev.filter((_, i) => i !== idx))
  }

  return (
    <Modal
      open={open}
      title={
        <div className="property-activities__modal-title">
          <div className="property-activities__modal-title-main">{titleText}</div>
          <div className="property-activities__modal-title-sub">{subtitleText}</div>
        </div>
      }
      onCancel={handleClose}
      closable={!loading}
      maskClosable={!loading}
      centered
      destroyOnClose
      width={520}
      className="property-activities__activity-modal"
      footer={() => (
        <Flex justify="flex-end" gap={8}>
          <Button disabled={loading} onClick={handleClose}>
            Cancel
          </Button>
          <Button color="primary" variant="solid" loading={loading} onClick={handleSubmit}>
            {isEdit ? 'Save Changes' : 'Create Activity'}
          </Button>
        </Flex>
      )}
    >
      <Form form={form} layout="vertical" className="property-activities__activity-form">
        <Form.Item
          label={
            <Flex justify="space-between" align="center" style={{ width: '100%' }}>
              <span>Activity Type</span>
              {!addTypeOpen && (
                <Button
                  type="link"
                  size="small"
                  icon={<PlusOutlined />}
                  onClick={() => setAddTypeOpen(true)}
                  style={{ padding: 0 }}
                >
                  Add Type
                </Button>
              )}
            </Flex>
          }
          name="typeId"
          rules={[{ required: true, message: 'Activity Type is required' }]}
          className="property-activities__form-label-block"
          hidden={addTypeOpen}
        >
          <Select
            placeholder="Select activity type"
            options={typeOptions}
            showSearch
            optionFilterProp="label"
          />
        </Form.Item>

        {addTypeOpen && (
          <div className="property-activities__add-type-panel">
            <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
              <span className="property-activities__add-type-title">Create New Type</span>
              <Button
                type="link"
                size="small"
                onClick={() => {
                  setAddTypeOpen(false)
                  setNewTypeName('')
                  setNewTypeIcon('')
                }}
                disabled={savingType}
                style={{ padding: 0 }}
              >
                Cancel
              </Button>
            </Flex>

            <div className="property-activities__add-type-label">Type Name</div>
            <Input
              placeholder="e.g., Construction Report"
              value={newTypeName}
              onChange={(e) => setNewTypeName(e.target.value)}
              maxLength={50}
              style={{ marginBottom: 12 }}
              disabled={savingType}
            />

            <div className="property-activities__add-type-label">Upload Icon</div>
            <Upload.Dragger
              accept=".svg,image/svg+xml"
              showUploadList={false}
              beforeUpload={beforeUploadIcon}
              disabled={uploadingIcon || savingType}
              className="property-activities__icon-dropzone"
            >
              {uploadingIcon ? (
                <Spin />
              ) : newTypeIcon ? (
                <Flex vertical align="center" gap={8}>
                  <Image
                    src={newTypeIcon}
                    alt="icon"
                    preview={false}
                    style={{ width: 48, height: 48, objectFit: 'cover', borderRadius: 8 }}
                  />
                  <span style={{ color: '#6a7282', fontSize: 12 }}>Click to replace</span>
                </Flex>
              ) : (
                <Flex vertical align="center" gap={4}>
                  <UploadOutlined style={{ fontSize: 24, color: '#9ca3af' }} />
                  <span style={{ fontSize: 13 }}>Click to upload icon</span>
                  <span style={{ fontSize: 11, color: '#9ca3af' }}>
                    SVG up to {MAX_ICON_SIZE_MB}MB
                  </span>
                </Flex>
              )}
            </Upload.Dragger>

            <Button
              block
              color="primary"
              variant="solid"
              loading={savingType}
              onClick={handleSaveType}
              style={{ marginTop: 12 }}
            >
              Save New Type
            </Button>
          </div>
        )}

        <Form.Item
          name="title"
          label="Title"
          rules={[
            { required: true, message: 'Title is required' },
            { max: TITLE_MAX, message: `Max ${TITLE_MAX} characters` },
          ]}
        >
          <Input placeholder="Title" maxLength={TITLE_MAX} showCount />
        </Form.Item>

        <Form.Item
          name="description"
          label="Description"
          rules={[
            { required: true, message: 'Description is required' },
            { max: DESCRIPTION_MAX, message: `Max ${DESCRIPTION_MAX} characters` },
          ]}
        >
          <MarkdownEditor placeholder="Description" height={240} />
        </Form.Item>

        <Form.Item
          name="timeline"
          label="Created Date"
          rules={[{ required: true, message: 'Created Date is required' }]}
        >
          <DatePicker
            style={{ width: '100%' }}
            format="YYYY-MM-DD"
            placeholder="Select created date"
          />
        </Form.Item>

        <Form.Item label={`Upload Images (${imageUrls.length}/${MAX_IMAGES})`}>
          <Dragger
            multiple
            accept={ALLOWED_FILE_EXT_IMAGE}
            showUploadList={false}
            beforeUpload={beforeUploadImage}
            disabled={imageUrls.length >= MAX_IMAGES}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click to upload files or drag and drop</p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              PNG, JPG up to {MAX_UPLOAD_FILE_SIZE_MB}MB (Multiple files allowed, max {MAX_IMAGES})
            </p>
          </Dragger>

          {(imageUrls.length > 0 || uploadingImages > 0) && (
            <Flex gap={8} wrap="wrap" style={{ marginTop: 12 }}>
              {imageUrls.map((url, idx) => (
                <div key={`${url}-${idx}`} className="property-activities__image-thumb">
                  <Image
                    src={url}
                    alt={`file-${idx + 1}`}
                    width={80}
                    height={80}
                    style={{ objectFit: 'cover', borderRadius: 6 }}
                  />
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={() => removeImage(idx)}
                    className="property-activities__image-thumb-remove"
                  />
                </div>
              ))}
              {Array.from({ length: uploadingImages }).map((_, idx) => (
                <div
                  key={`uploading-${idx}`}
                  className="property-activities__image-thumb is-loading"
                >
                  <Spin />
                </div>
              ))}
            </Flex>
          )}
        </Form.Item>

        <Form.Item label={`Upload Documents (${documentUrls.length}/${MAX_DOCUMENTS})`}>
          <Dragger
            multiple
            accept={ALLOWED_FILE_EXT_PDF}
            showUploadList={false}
            beforeUpload={beforeUploadDocument}
            disabled={documentUrls.length >= MAX_DOCUMENTS}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">Click to upload files or drag and drop</p>
            <p className="ant-upload-hint" style={{ fontSize: 12 }}>
              PDF up to {MAX_UPLOAD_FILE_SIZE_MB}MB (Multiple files allowed, max {MAX_DOCUMENTS})
            </p>
          </Dragger>

          {(documentUrls.length > 0 || uploadingDocuments > 0) && (
            <Flex gap={8} wrap="wrap" style={{ marginTop: 12 }}>
              {documentUrls.map((url, idx) => (
                <a
                  key={`${url}-${idx}`}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="property-activities__image-thumb is-pdf"
                  title={getFileNameFromUrl(url)}
                >
                  <FilePdfOutlined style={{ fontSize: 28, color: '#ef4444' }} />
                  <span className="property-activities__pdf-thumb-name">
                    {getFileNameFromUrl(url)}
                  </span>
                  <Button
                    size="small"
                    type="text"
                    icon={<CloseOutlined />}
                    onClick={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                      removeDocument(idx)
                    }}
                    className="property-activities__image-thumb-remove"
                  />
                </a>
              ))}
              {Array.from({ length: uploadingDocuments }).map((_, idx) => (
                <div
                  key={`uploading-${idx}`}
                  className="property-activities__image-thumb is-loading"
                >
                  <Spin />
                </div>
              ))}
            </Flex>
          )}
        </Form.Item>
      </Form>
    </Modal>
  )
}
